/**
 * Utility functions for activity display
 */
import i18n from '../i18n';

/**
 * Gets the display title for an activity based on the current language.
 * Uses translated_titles if available, falls back to the original title.
 * 
 * @param {Object} activity - The activity object
 * @returns {string} - The title to display
 */
export function getActivityDisplayTitle(activity) {
    if (!activity) return '';
    
    const currentLang = i18n.language || 'en';
    
    // Check if translated_titles exists and has a translation for the current language
    if (activity.translated_titles) {
        // Parse if it's a string (from database)
        let translations = activity.translated_titles;
        if (typeof translations === 'string') {
            try {
                translations = JSON.parse(translations);
            } catch (e) {
                // If parsing fails, use the original title
                return activity.title || '';
            }
        }
        
        // Try to get translation for current language
        if (translations && translations[currentLang]) {
            return translations[currentLang];
        }
        
        // Try 2-letter language code if full code doesn't match (e.g., 'en-US' -> 'en')
        const shortLang = currentLang.split('-')[0];
        if (translations && translations[shortLang]) {
            return translations[shortLang];
        }
    }
    
    // Fall back to original title
    return activity.title || '';
}

/**
 * Gets the original (English) title for an activity.
 * This should be used for affiliate API calls (Viator, GYG, etc.)
 * 
 * @param {Object} activity - The activity object
 * @returns {string} - The original English title
 */
export function getActivityOriginalTitle(activity) {
    if (!activity) return '';
    return activity.title || '';
}

/**
 * Checks if an activity has translations available
 * 
 * @param {Object} activity - The activity object
 * @returns {boolean} - True if translations are available
 */
export function hasTranslations(activity) {
    if (!activity || !activity.translated_titles) return false;
    
    let translations = activity.translated_titles;
    if (typeof translations === 'string') {
        try {
            translations = JSON.parse(translations);
        } catch (e) {
            return false;
        }
    }
    
    return translations && Object.keys(translations).length > 0;
}
